import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Inbox, Loader2, Phone, Mail, MapPin, Users, RefreshCw, MessageCircle, BellRing } from 'lucide-react';
import { API } from '../../../lib/api';
import { waUrl } from '../../../lib/waShare';
import { useAuth } from '../../../contexts/AuthContext';

const STATUSES = [
  { key: '', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'converted', label: 'Signed up' },
  { key: 'dropped', label: 'Not now' },
];

const TONE = {
  new: 'bg-sky-100 text-sky-700',
  contacted: 'bg-amber-100 text-amber-700',
  converted: 'bg-teal-100 text-teal-700',
  dropped: 'bg-slate-200 text-slate-600',
};

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

const slotText = (slot) => new Date(`${slot}:00+05:30`).toLocaleString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' });

const waLink = (l) => {
  const digits = String(l.phone).replace(/\D/g, '').slice(-10);
  const text = `Hi ${l.contact_name}, this is the Driveline team from Unisol Global. A quick reminder that your Driveline demo is on ${slotText(l.demo_slot)} IST. We will call you on this number. Reply here if you need another time.`;
  return waUrl(digits, text);
};

const TYPE_LABEL = { detailing: 'Detailing / PPF', bodyshop: 'Body shop', multibrand: 'Multi-brand service', showroom: 'Showroom', other: 'Other' };
const isUpcoming = (l) => new Date(`${l.demo_slot}:00+05:30`) > new Date() && l.status !== 'dropped';

const DemoReminder = ({ l }) => (
  <div className="mt-1.5 flex flex-wrap items-center gap-2">
    {isUpcoming(l) && <a href={waLink(l)} target="_blank" rel="noreferrer" data-testid={`lead-wa-remind-${l.id}`}
       className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-2.5 py-0.5 text-xs font-semibold text-white hover:bg-emerald-700">
      <MessageCircle size={12} /> WhatsApp reminder
    </a>}
    {l.reminder_sent_at && (
      <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-testid={`lead-reminder-sent-${l.id}`}>
        <BellRing size={12} /> Email reminder sent {when(l.reminder_sent_at)}
      </span>
    )}
  </div>
);

/** Driveline's enquiry inbox: who asked for a call back, and where each one got to. */
const LeadsInbox = () => {
  const { authHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');

  const load = useCallback(async (status) => {
    const { data } = await axios.get(`${API}/platform/leads`, {
      headers: authHeader(), params: status ? { status } : {},
    });
    setRows(data.leads || []);
    setCounts(data.counts || {});
    setLoading(false);
  }, [authHeader]);

  useEffect(() => { load(filter); }, [load, filter]);

  const setStatus = async (id, status) => {
    setSavingId(id);
    try {
      await axios.patch(`${API}/platform/leads/${id}`, { status }, { headers: authHeader() });
      await load(filter);
    } finally {
      setSavingId('');
    }
  };

  const newCount = useMemo(() => counts.new || 0, [counts]);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500">
        <Loader2 className="animate-spin" size={18} /> Loading enquiries…
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="leads-inbox">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Inbox size={22} className="text-teal-600" /> Driveline enquiries
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {newCount > 0
              ? `${newCount} waiting for a call back.`
              : 'Everyone has been followed up.'}
          </p>
        </div>
        <button
          onClick={() => load(filter)}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg px-3 py-2"
          data-testid="leads-refresh"
        >
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="leads-filters">
        {STATUSES.map((s) => (
          <button
            key={s.key || 'all'}
            onClick={() => setFilter(s.key)}
            className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
              filter === s.key ? 'bg-teal-500 text-white border-teal-500'
                               : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'}`}
            data-testid={`leads-filter-${s.key || 'all'}`}
          >
            {s.label}{s.key && counts[s.key] ? ` (${counts[s.key]})` : ''}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500"
             data-testid="leads-empty">
          No enquiries here yet. They arrive from the Talk to us form on the Driveline page.
        </div>
      ) : (
        <div className="space-y-3" data-testid="leads-list">
          {rows.map((l) => (
            <div key={l.id} className="rounded-2xl border border-slate-200 bg-white p-5"
                 data-testid={`lead-row-${l.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base font-semibold text-slate-900">{l.workshop_name}</h2>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${TONE[l.status] || TONE.new}`}>
                      {STATUSES.find((s) => s.key === l.status)?.label || l.status}
                    </span>
                    {l.interested_in && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        {String(l.interested_in).replace('_', ' ')}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{l.contact_name}</p>
                  {l.demo_slot && (
                    <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-2.5 py-0.5 text-xs font-semibold text-teal-800" data-testid={`lead-demo-slot-${l.id}`}>
                      Demo booked · {slotText(l.demo_slot)} IST
                      {l.workshop_type ? ` · ${TYPE_LABEL[l.workshop_type] || l.workshop_type}` : ''}
                    </p>
                  )}
                  {l.demo_slot && <DemoReminder l={l} />}
                  {l.cancelled_slot && (
                    <p className="mt-1.5 inline-flex rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700" data-testid={`lead-demo-cancelled-${l.id}`}>
                      Demo cancelled by prospect · was {slotText(l.cancelled_slot)} IST
                    </p>
                  )}
                  {l.rescheduled_at && l.demo_slot && <p className="mt-1 text-xs text-slate-500" data-testid={`lead-demo-rescheduled-${l.id}`}>Rescheduled by prospect {when(l.rescheduled_at)}</p>}
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">
                    <a href={`tel:${String(l.phone).replace(/\s/g, '')}`}
                       className="inline-flex items-center gap-1.5 text-teal-700 hover:underline"
                       data-testid={`lead-call-${l.id}`}>
                      <Phone size={14} /> {l.phone}
                    </a>
                    {l.email && (
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:underline">
                        <Mail size={14} /> {l.email}
                      </a>
                    )}
                    {l.city && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {l.city}</span>}
                    {l.team_size && <span className="inline-flex items-center gap-1.5"><Users size={14} /> {l.team_size} people</span>}
                    {l.bays && <span className="inline-flex items-center gap-1.5" data-testid={`lead-bays-${l.id}`}><Users size={14} /> {l.bays}</span>}
                  </div>
                  {l.message && (
                    <p className="mt-3 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 whitespace-pre-wrap">
                      {l.message}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-slate-400">{when(l.created_at)}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5 justify-end">
                    {['new', 'contacted', 'converted', 'dropped'].map((s) => (
                      <button
                        key={s}
                        disabled={savingId === l.id || l.status === s}
                        onClick={() => setStatus(l.id, s)}
                        className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${
                          l.status === s ? 'bg-slate-900 text-white border-slate-900'
                                         : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'}`}
                        data-testid={`lead-set-${s}-${l.id}`}
                      >
                        {STATUSES.find((x) => x.key === s)?.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadsInbox;
